import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { USERS_API_URL } from '../../config/apiConfig';
import './ResetPasswordScreen.css';

const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams();
  const [token] = useState(searchParams.get('token'));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [passwordPolicies, setPasswordPolicies] = useState([]);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
      setTokenValid(false);
      return;
    }

    // Verify token validity
    const verifyToken = async () => {
      try {
        const response = await fetch(`${USERS_API_URL}/auth/verify-reset-token?token=${token}`);
        const data = await response.json();
        
        if (response.ok) {
          setTokenValid(true);
          // Get password policies
          const policiesResponse = await fetch(`${USERS_API_URL}/auth/password-policies`);
          const policiesData = await policiesResponse.json();
          if (policiesResponse.ok) {
            setPasswordPolicies(policiesData.policies);
          }
        } else {
          setError(data.error || 'Invalid or expired reset token');
          setTokenValid(false);
        }
      } catch (err) {
        setError('Failed to verify reset token');
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${USERS_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          token, 
          newPassword 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (tokenValid === false) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="error-message-full">
            <div className="error-icon">⚠️</div>
            <h2>Invalid Reset Link</h2>
            <p>{error}</p>
            <p>This reset link may have expired or is invalid. Please try requesting a new password reset.</p>
            <button 
              className="back-to-login-button"
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Password Reset Successfully</h2>
            <p>
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <button 
              className="back-to-login-button"
              onClick={handleBackToLogin}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="reset-password-screen">
        <div className="reset-password-container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-screen">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <h1>Set New Password</h1>
          <p>Please enter your new password below.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="reset-password-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {passwordPolicies.length > 0 && (
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                {passwordPolicies.map((policy, index) => (
                  <li key={index} className={
                    newPassword && 
                    ((policy.type === 'min_length' && newPassword.length >= policy.value) ||
                     (policy.type === 'require_uppercase' && /[A-Z]/.test(newPassword)) ||
                     (policy.type === 'require_lowercase' && /[a-z]/.test(newPassword)) ||
                     (policy.type === 'require_numbers' && /\d/.test(newPassword)) ||
                     (policy.type === 'require_special_chars' && /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)))
                    ? 'policy-met' : 'policy-unmet'
                  }>
                    {policy.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your new password"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Confirm your new password"
            />
          </div>
          
          <div className="button-group">
            <button 
              type="submit" 
              className="reset-button"
              disabled={loading || newPassword !== confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleBackToLogin}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
